/**
 * 🎓 v7.41 (per Javi, proposal §3 C "First location"): the guide that walks
 * a FIRST RUN through its first location - one line at a time, beside what
 * it names, with the tap-hand on the thing to tap (PracticeCoach). It lasts
 * until location 1 is cleared (RunState.firstRun) - whatever the run's tips
 * flag says; "I've played before" starts without the coach, and the menu's
 * "Stop guidance" ends it later. Everyone else's game
 * is untouched: every rule here starts from `firstRun`.
 *
 * Pure on purpose (the node tests drive it): the odds are the mission
 * sheet's own (missionTeamStats -> successChance -> adjustedTeamChance), so
 * "a monster your free creatures can win" means what the sheet would show.
 *
 * What it remembers rides the run's `tutorialSeen` list as `coach:*` ids,
 * beside the 'guide:*' marks v7.40 keeps there - no new save field, no new
 * storage key, and a restarted first run starts the guide over.
 *
 * (Every export is coach-prefixed: app/utils is auto-imported by name.)
 */
import type { OnboardingPreference } from '~/composables/useOnboarding'
import { adjustedTeamChance, creatureSkill, isBossSplit, missionTeamStats, slotCapacity, slotsUsed, successChance, surgeCellMult, withPredictedRoutine } from '~/utils/battle'
import { ELEMENTS, type Creature, type ElementKey, type Mission, type RunState, type Stats } from '~/types/game'

/** The guide's marks in `tutorialSeen`. */
export const COACH = {
  /** "I've played before" or "Stop guidance": no step-by-step coach; the one-liners stay. */
  off: 'coach:off',
  arrive: 'coach:arrive',
  partial: 'coach:partial',
  wheel: 'coach:wheel',
  cleared: 'coach:cleared',
  prize: 'coach:prize',
} as const

/** The first-time one-liners: one queue, one at a time, each once per first run.
 * ('surge' and 'discard' speak inside the sheet, where their buttons are.
 * 🎓 v7.41 review: 'discard' - the controls card's third lesson, beside camp
 * and ⏩ - so the card the arrival holds back is split into one-liners, as
 * the proposal says.) */
export const COACH_LINERS = ['surge', 'discard', 'gold', 'plus', 'pause', 'expiry', 'enrage', 'ff'] as const
export type CoachLiner = typeof COACH_LINERS[number]
export const coachLinerMark = (id: CoachLiner) => `coach:${id}`

/** The classic tips a first run holds back (useTutorial.stepFor), the WHOLE
 * first run: the late tips the one-liners stand in for (they keep coming
 * after "Stop guidance") - a first run's late tip IS its one-liner - and
 * "That one hurt", which the rails' failure card says in its own words. */
export const COACH_QUIET: readonly string[] = ['gold', 'levelup', 'failure', 'enrage']
/** ...and the ones held back only WHILE THE COACH IS ON (coachOn): the arrival
 * pair and the sheet card (the arrival card and the sheet's lines say it),
 * "Mission complete!" (Location cleared! says it). 🎓 v7.41 (integration
 * review): after "Stop guidance" none of those replacements show, so these
 * classic cards come back - location 1 never goes silent. (While it is on,
 * the controls card - camp, ⏩, discard - is three one-liners: pause, ff, discard.) */
export const COACH_GUIDED_QUIET: readonly string[] = ['encounter', 'banner', 'mission', 'done']
/** Does a first run hold this classic tip back right now? */
export function coachHoldsTip(run: Partial<GuideRun> | null | undefined, id: string): boolean {
  if (!coachFirstRun(run)) return false
  return COACH_QUIET.includes(id) || (COACH_GUIDED_QUIET.includes(id) && coachOn(run))
}
/** The classic late tip each one-liner teaches: marked seen with it, so the
 * card never repeats the lesson after location 1. */
export const COACH_LINER_TIP: Partial<Record<CoachLiner, string>> = { gold: 'gold', plus: 'levelup', enrage: 'enrage' }

type GuideRun = Pick<RunState, 'firstRun' | 'mode' | 'tutorial' | 'tutorialSeen'> & { gauntlet?: boolean; puzzleLevel?: number }

/** A campaign first run (never the practice, a weekly, the Gauntlet or a Puzzle Game). */
export function coachFirstRun(run: Partial<GuideRun> | null | undefined): boolean {
  return run?.firstRun === true && !run.mode && !run.gauntlet && typeof run.puzzleLevel !== 'number'
}
/** A first run's guide plays: its one-liners, and the coach below.
 * 🎓 v7.41 (review): on the first run alone - whatever the run's `tutorial`
 * flag says. A practice graduate who lost (or left) location 1 restarts with
 * tips off (tutorialAlreadyDone), and those are the players who need the
 * guide most; a classic card's skip link (a mindless tap) does not end it
 * either. The menu's "Stop guidance" is the way out (coachOn). */
export function coachTips(run: Partial<GuideRun> | null | undefined): boolean {
  return coachFirstRun(run)
}
/** Apply the welcome answer before publishing a new run. The existing mark
 * travels with saves; practice graduates keep the coach and the one-liners. */
export function coachStart(run: Partial<GuideRun> | null | undefined, preference: OnboardingPreference): void {
  if (coachFirstRun(run) && preference === 'dismissed') markCoach(run, COACH.off)
}
/** ...and the step-by-step coach, unless declined or stopped. */
export function coachOn(run: Partial<GuideRun> | null | undefined): boolean {
  return coachTips(run) && !coachSeen(run, COACH.off)
}
export function coachSeen(run: Partial<GuideRun> | null | undefined, id: string): boolean {
  return !!run?.tutorialSeen?.includes(id)
}
export function markCoach(run: Partial<GuideRun> | null | undefined, id: string): void {
  if (run && Array.isArray(run.tutorialSeen) && !run.tutorialSeen.includes(id)) run.tutorialSeen.push(id)
}
export function unmarkCoach(run: Partial<GuideRun> | null | undefined, id: string): void {
  if (!run || !Array.isArray(run.tutorialSeen)) return
  const at = run.tutorialSeen.indexOf(id)
  if (at >= 0) run.tutorialSeen.splice(at, 1)
}

/** A creature the player could send right now (the sheet's own test, bosses aside). */
export function coachFree(creature: Creature): boolean {
  return creature.available && !creature.benched && !((creature.benchSeconds ?? 0) > 0)
}

/** The sheet's chance for this team (without one-shot boosts: journal, tithe...). */
export function coachTeamChance(mission: Mission, team: Creature[], run?: RunState | null): number {
  if (!team.length) return 0
  const predicted = withPredictedRoutine(team, run)
  const stats = missionTeamStats(mission, predicted, run)
  const base = successChance(mission.required, stats, run, undefined, team.length)
  return adjustedTeamChance(base, predicted, {}, run, mission)
}

/** The best chance `creatures` can reach on `mission`, over every team that
 * fits its slots (a first run fields four: at most fifteen teams). */
export function coachBestChance(mission: Mission, creatures: Creature[], run?: RunState | null): { chance: number; team: Creature[] } {
  let best = { chance: 0, team: [] as Creature[] }
  const pool = creatures.slice(0, 8)
  for (let mask = 1; mask < 1 << pool.length; mask++) {
    const team = pool.filter((_, i) => mask & (1 << i))
    if (slotsUsed(team) > slotCapacity(mission, team, run)) continue
    const chance = coachTeamChance(mission, team, run)
    if (chance > best.chance || (chance === best.chance && team.length < best.team.length)) best = { chance, team }
  }
  return best
}

/** What a monster asks for, the biggest ask first. */
export function coachAsks(mission: Pick<Mission, 'required'>): ElementKey[] {
  return ELEMENTS.filter(e => mission.required[e] > 0)
    .sort((a, b) => mission.required[b] - mission.required[a])
}
/** The name the coach calls a monster by: its biggest ask - or both, when
 * the second is at least half the first ("the Fire and Water monster").
 * 🎓 v7.41 (review): two names read in the monster's own badge order
 * (StatChips: ELEMENTS order), so the words match the badge left to right. */
export function coachNames(mission: Pick<Mission, 'required'>): ElementKey[] {
  const asks = coachAsks(mission)
  const named = asks.length >= 2 && mission.required[asks[1]!] * 2 >= mission.required[asks[0]!] ? asks.slice(0, 2) : asks.slice(0, 1)
  return named.sort((a, b) => ELEMENTS.indexOf(a) - ELEMENTS.indexOf(b))
}

/** A monster the free creatures win at least this often (the sheet's %) is
 * one the coach may point at. Below it, letting it go costs less. */
export const COACH_WINNABLE = 50

/** A monster the coach may name: a regular waiting one (gold ones have
 * their own line, and never count toward the goal). */
export function coachable(mission: Mission): boolean {
  return mission.status === 'waiting' && !mission.goldReward && !mission.training && !mission.puzzle
    && !mission.minion && !isBossSplit(mission) && !mission.tutorialLesson
}

/**
 * 🎓 v7.41: the monster the coach points at - ONLY one the free creatures
 * can win (COACH_WINNABLE or better, the sheet's own odds), the surest
 * first, then the one closest to running out. Null: nothing to point at.
 */
export function coachTarget(missions: Mission[], roster: Creature[], run?: RunState | null): { mission: Mission; chance: number } | null {
  const free = roster.filter(coachFree)
  if (!free.length) return null
  const scored = missions.filter(coachable)
    .map(mission => ({ mission, chance: coachBestChance(mission, free, run).chance }))
    .filter(s => s.chance >= COACH_WINNABLE)
    .sort((a, b) => (b.chance - a.chance) || (a.mission.expireTimer - b.mission.expireTimer))
  return scored[0] ?? null
}

/**
 * 🎓 v7.41 (review: an "I've played before" first-timer opened a sheet to 0%,
 * a small "pick your team" and four Surge bars - no line): the creature a
 * first run's sheet names next, "Tap Stoneback: it has Earth!" - one of the
 * best team the free creatures can field here (coachBestChance) that is not
 * aboard yet, fits the slots and lifts the sheet's odds, the biggest lift
 * first; and the monster's element it brings the most of - counted only up to
 * what the monster asks (🎓 v7.41 integration review: Mirrorfin's Water 3
 * "brought" 3 to a Water 1 / Earth 3 monster the board had just called "the
 * Earth monster"; it really covers 1 Water and 2 Earth), a tie going to the
 * element the board's line named (coachNames). Null when the team aboard is
 * already as good as it gets, or nobody can win it.
 * (What a creature "has" is what it adds to the sheet's TEAM total - the
 * team-context passives included: Pebblet's Underdog brings +1 of every
 * element, and was the only helper a Water monster had - the sheet named
 * no one. A creature that only lifts the odds, no element, is never named.)
 */
export function coachPick(mission: Mission, free: Creature[], picked: Creature[], run?: RunState | null): { creature: Creature; element: ElementKey } | null {
  const best = coachBestChance(mission, free, run)
  const now = picked.length ? coachTeamChance(mission, picked, run) : 0
  if (best.chance <= 0 || now >= best.chance) return null
  const asks = coachAsks(mission)
  const named = coachNames(mission)
  const aboard = new Set(picked.map(c => c.id))
  const totals = (team: Creature[]) => missionTeamStats(mission, withPredictedRoutine(team, run), run)
  const before = totals(picked)
  const lifts = best.team.filter(c => !aboard.has(c.id))
    .filter(c => slotsUsed([...picked, c]) <= slotCapacity(mission, [...picked, c], run))
    .map((c) => {
      const after = totals([...picked, c])
      // What it covers of each ask (the sheet's odds count no more than the ask).
      const covers = (e: ElementKey) => Math.min(after[e], mission.required[e]) - Math.min(before[e], mission.required[e])
      const brings = [...asks].sort((a, b) => (covers(b) - covers(a)) || (Number(named.includes(b)) - Number(named.includes(a))))[0]!
      return { creature: c, brings, adds: covers(brings), gain: coachTeamChance(mission, [...picked, c], run) - now }
    })
    .filter(l => l.gain > 0 && l.adds > 0)
    .sort((a, b) => (b.gain - a.gain) || (b.adds - a.adds))
  const pick = lifts[0]
  return pick ? { creature: pick.creature, element: pick.brings } : null
}

/** The element a team is shortest of on a monster (the biggest gap). */
export function coachShortfall(required: Stats, team: Stats): { element: ElementKey; need: number; have: number } | null {
  const short = ELEMENTS.filter(e => required[e] > 0 && team[e] < required[e])
    .sort((a, b) => ((required[b] - team[b]) - (required[a] - team[a])) || (required[b] - required[a]))[0]
  return short ? { element: short, need: required[short], have: Math.max(0, team[short]) } : null
}

/** A picked creature whose Surge would lift this fight - the first one that
 * helps - and what it adds. */
export function coachSurge(mission: Mission, picked: Creature[], run?: RunState | null): { creature: Creature; element: ElementKey; amount: number } | null {
  const now = coachTeamChance(mission, picked, run)
  if (now >= 100 || !picked.length) return null
  const team = missionTeamStats(mission, withPredictedRoutine(picked, run), run)
  for (const creature of picked) {
    if (creature.surgePrimed || creature.surgeCooldown > 0 || !creature.available) continue
    const skill = creatureSkill(creature, run)
    if (skill.kind === 'none') continue
    const armed = picked.map(c => (c.id === creature.id ? { ...c, surgePrimed: true } : c))
    if (coachTeamChance(mission, armed, run) <= now) continue
    const element = skill.elements.find(e => mission.required[e] > team[e]) ?? skill.elements[0]
    if (!element) continue
    return { creature, element, amount: skill.bonus * surgeCellMult(skill, run) }
  }
  return null
}
